import { useTranslation } from "react-i18next";
import { CheckCircle2, HelpCircle, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  useDevicePermission,
  type DeviceKind,
} from "@/hooks/useDevicePermission";

/**
 * Where a device stands with the browser, said beside the device list.
 *
 * The button is the point: the room asks for a device when it needs one, and
 * on browsers where that prompt never appears the visitor had nothing to press
 * and no way to know they had been refused.
 */
export function DevicePermissionRow({
  kind,
  onGranted,
}: {
  kind: DeviceKind;
  /** Device names only become readable once access is given. */
  onGranted?: () => void;
}) {
  const { t } = useTranslation();
  const { access, asking, ask } = useDevicePermission(kind, onGranted);

  const Icon =
    access === "granted"
      ? CheckCircle2
      : access === "refused"
        ? ShieldAlert
        : HelpCircle;

  const tone =
    access === "granted"
      ? "text-audio-active"
      : access === "refused"
        ? "text-destructive"
        : "text-muted-foreground";

  return (
    <div
      className="flex items-center justify-between gap-2"
      data-testid={`permission-${kind}`}
    >
      <span className={`flex items-center gap-1.5 text-xs ${tone}`}>
        <Icon className="w-3.5 h-3.5" aria-hidden />
        {t(`settings.permission.${access}`)}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        disabled={asking}
        onClick={() => void ask()}
      >
        {t("settings.permission.ask")}
      </Button>
    </div>
  );
}
